import { interpretarNumeroProcesso, type ResultadoConsulta } from "@consulta/core";
import { useMemo, useState } from "react";
import { consultar } from "../api.js";
import { ResumoProcessoView } from "./ResumoProcesso.js";

const EXEMPLO = "4003366-97.2026.8.26.0541";

export function ConsultaIndividual() {
  const [entrada, setEntrada] = useState("");
  const [incluirSuperiores, setIncluirSuperiores] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [resultado, setResultado] = useState<ResultadoConsulta | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  // Valida enquanto o usuário digita, para avisar antes de gastar uma consulta.
  const previa = useMemo(() => {
    if (entrada.trim() === "") return null;
    const r = interpretarNumeroProcesso(entrada);
    if (!r.ok) return { tipo: "erro" as const, texto: r.erro };
    const { numero } = r;
    if (numero.tribunais.length === 0) {
      return {
        tipo: "alerta" as const,
        texto: `Número válido, mas o par segmento ${numero.segmento} / código ${numero.codigoTribunal} não corresponde a nenhum tribunal conhecido.`,
      };
    }
    const onde = numero.tribunais.map((t) => t.sigla).join(" ou ");
    if (!numero.digitoVerificadorValido) {
      return {
        tipo: "alerta" as const,
        texto: `O dígito verificador não confere — confira a digitação. Será consultado em ${onde}.`,
      };
    }
    return { tipo: "ok" as const, texto: `${numero.formatado} — ${onde}` };
  }, [entrada]);

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    setCarregando(true);
    setErro(null);
    setResultado(null);
    try {
      setResultado(await consultar(entrada, { incluirSuperiores }));
    } catch (e) {
      setErro(e instanceof Error ? e.message : "Falha ao consultar.");
    } finally {
      setCarregando(false);
    }
  }

  return (
    <div className="pilha">
      <section className="cartao">
        <div className="cartao-corpo">
          <form onSubmit={enviar} className="pilha-sm">
            <div>
              <label className="rotulo" htmlFor="numero-processo">
                Número do processo
              </label>
              <div className="linha-campo">
                <input
                  id="numero-processo"
                  className="campo campo-numero"
                  value={entrada}
                  onChange={(e) => setEntrada(e.target.value)}
                  placeholder={EXEMPLO}
                  autoComplete="off"
                  spellCheck={false}
                  inputMode="numeric"
                  aria-describedby="dica-numero"
                />
                <button className="botao" type="submit" disabled={carregando || entrada.trim() === ""}>
                  {carregando && <span className="girando" aria-hidden="true" />}
                  {carregando ? "Consultando" : "Consultar"}
                </button>
              </div>
              <p className="dica" id="dica-numero" style={{ marginTop: 6 }}>
                Com ou sem máscara. O tribunal é identificado pelo próprio número.{" "}
                <button
                  type="button"
                  className="botao-discreto"
                  onClick={() => setEntrada(EXEMPLO)}
                  style={{ padding: 0 }}
                >
                  usar um exemplo
                </button>
              </p>
            </div>

            {previa && (
              <div
                className={`aviso aviso-${previa.tipo === "ok" ? "info" : previa.tipo}`}
                role={previa.tipo === "erro" ? "alert" : undefined}
              >
                {previa.texto}
              </div>
            )}

            <label className="caixa-selecao">
              <input
                type="checkbox"
                checked={incluirSuperiores}
                onChange={(e) => setIncluirSuperiores(e.target.checked)}
              />
              <span>
                Procurar também nos tribunais superiores (STJ, STF/STM, TST, TSE)
                <br />
                <span className="dica">
                  Eles conservam o número de origem do processo, então não dá para deduzi-los. Torna a consulta mais lenta.
                </span>
              </span>
            </label>
          </form>
        </div>
      </section>

      {erro && (
        <div className="aviso aviso-erro" role="alert">
          {erro}
        </div>
      )}

      {resultado?.status === "encontrado" && <ResumoProcessoView resumo={resultado.resumo} />}

      {resultado?.status === "nao_encontrado" && (
        <div className="aviso aviso-alerta" role="status">
          {resultado.mensagem}
        </div>
      )}

      {resultado?.status === "invalido" && (
        <div className="aviso aviso-erro" role="alert">
          {resultado.mensagem}
        </div>
      )}

      {resultado?.status === "erro" && (
        <div className="aviso aviso-erro" role="alert">
          {resultado.mensagem}
        </div>
      )}
    </div>
  );
}
